import React from 'react';
import { Card, InlineStack, BlockStack, Text, Box, Badge } from '@shopify/polaris';
import { accountingHelpers } from '../../constants/accountingConstants';

/**
 * Financial Summary Card Component
 * Displays key financial metrics in a card format
 */
export const FinancialSummaryCard = ({
    title,
    amount,
    subtitle,
    trend,
    trendLabel,
    currency = '',
    status = 'default',
    icon
}) => {
    const getTrendBadge = () => {
        if (!trend) return null;

        const isPositive = trend > 0;
        const badge = status === 'revenue'
            ? (isPositive ? 'success' : 'critical')
            : status === 'expense'
                ? (isPositive ? 'critical' : 'success')
                : (isPositive ? 'success' : 'critical');

        return (
            <Badge tone={badge}>
                {isPositive ? '↑' : '↓'} {Math.abs(trend).toFixed(1)}%
            </Badge>
        );
    };

    const getAmountColor = () => {
        if (status === 'profit') {
            return accountingHelpers.getProfitLossColor(amount);
        }
        return undefined;
    };

    return (
        <Card>
            <BlockStack gap="200">
                <InlineStack align="space-between" blockAlign="center">
                    <Text variant="headingSm" as="h3" tone="subdued">
                        {title}
                    </Text>
                    {icon && <Box>{icon}</Box>}
                </InlineStack>

                <Text variant="heading2xl" as="p" tone={getAmountColor()}>
                    {accountingHelpers.formatCurrency(amount, currency)}
                </Text>

                <InlineStack gap="200" blockAlign="center">
                    {subtitle && (
                        <Text variant="bodySm" tone="subdued">
                            {subtitle}
                        </Text>
                    )}
                    {trend !== undefined && trend !== null && (
                        <>
                            {getTrendBadge()}
                            {trendLabel && (
                                <Text variant="bodySm" tone="subdued">
                                    {trendLabel}
                                </Text>
                            )}
                        </>
                    )}
                </InlineStack>
            </BlockStack>
        </Card>
    );
};

/**
 * Profit Margin Card Component
 * Displays profit margin with color coding
 */
export const ProfitMarginCard = ({ title, percentage, subtitle }) => {
    const marginColor = accountingHelpers.getMarginColor(percentage);

    return (
        <Card>
            <BlockStack gap="200">
                <Text variant="headingSm" as="h3" tone="subdued">
                    {title}
                </Text>

                <Text variant="heading2xl" as="p" tone={marginColor}>
                    {accountingHelpers.formatPercentage(percentage)}
                </Text>

                {subtitle && (
                    <Text variant="bodySm" tone="subdued">
                        {subtitle}
                    </Text>
                )}
            </BlockStack>
        </Card>
    );
};

/**
 * Financial Metrics Grid Component
 * Displays multiple financial metrics in a grid
 */
export const FinancialMetricsGrid = ({ metrics }) => {
    return (
        <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1rem'
        }}>
            {metrics.map((metric, index) => {
                if (metric.type === 'percentage') {
                    return (
                        <ProfitMarginCard
                            key={index}
                            title={metric.title}
                            percentage={metric.value}
                            subtitle={metric.subtitle}
                        />
                    );
                }

                return (
                    <FinancialSummaryCard
                        key={index}
                        title={metric.title}
                        amount={metric.value}
                        subtitle={metric.subtitle}
                        trend={metric.trend}
                        trendLabel={metric.trendLabel}
                        currency={metric.currency}
                        status={metric.status}
                        icon={metric.icon}
                    />
                );
            })}
        </div>
    );
};

export default FinancialSummaryCard;
